import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  distinctUntilChanged,
  filter,
  switchMap,
  map,
  scan,
  shareReplay,
  take,
  withLatestFrom,
  tap
} from 'rxjs/operators'
import { combineLatest, merge, Observable, Subject, ReplaySubject } from 'rxjs'
import { environment } from '../../../environments/environment'
import { VerticalService } from '../../singleton-services/vertical.service'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService,
  removeTrailingSlash
} from '../../libs/page-meta/page-meta.service'
import { PageMeta } from '../../libs/page-meta/page-meta.interface'
import { SegmentTagV2 } from '../../shared/analytics/models/segment-tag.model'
import { SegmentEvents } from '../../shared/analytics/models/segment-events.model'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTypes } from '../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_SKIN_SIZE
} from '../../shared/ads/ads.utility'
import {
  accumulateViewAll,
  getRouteQueryParams,
  shouldShowViews
} from '../../singleton-services/category.service.utility'
import { CategoryService } from '../../singleton-services/category.service'
import { CategoryView } from '../../libs/category-list/category-list.interface'
import { capitalizeFirstChar } from '../../shared/utility-functions/string.utility'
import { SearchMeta, SearchQueryParams } from '../../singleton-services/search-data.service.interface'
import { PlatformService } from '../../singleton-services/platform.service'

export const ATHLETE_LIST_TAG: SegmentTagV2 = {
  page_category: 'People',
  subpage_category: 'People List',
  category: 'People',
  autoPageCall: false
}

@Component({
  templateUrl: './athletes-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AthletesListComponent implements OnInit, OnDestroy {
  // When the user interacts with the page (search/facets) this will trigger
  private readonly queryChangeSubject = new Subject<void>()
  // Page Meta used for infinite scroll
  private readonly pageMetaSubject = new ReplaySubject<SearchMeta | undefined>()
  // Fetching more athletes for infinite scroll
  public readonly fetchAthletes = new Subject<void>()
  public readonly type = 'athlete'
  // Page marker to trigger/reset infinite scroll
  private page = 1

  // prettier ignore
  constructor(
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService,
    private readonly platformService: PlatformService,
    private readonly catService: CategoryService,
    private readonly pageMeta: PageMetaService,
    private readonly segment: SegmentService,
    private readonly vs: VerticalService,
    private readonly ar: ActivatedRoute,
    private readonly router: Router
  ) {}

  private distinctQueryParams$ = this.ar.queryParamMap.pipe(
    filter(() => this.platformService.isBrowser), // don't need ssr loaded data - don't call service twice
    map(queryParams => getRouteQueryParams(queryParams)),
    distinctUntilChanged((a: SearchQueryParams, b: SearchQueryParams) => {
      // Will be helpful when facets are added a.q === b.q && a.facets === b.facets
      return a.q === b.q
    }),
    tap(_ => {
      this.page = 1 // query changed, reset infinite scroll pagination index
    }),
    shareReplay(1)
  )

  // Should only fire once
  private initialAthletes$ = this.ar.data.pipe(
    filter(data => data !== undefined),
    map((res: { athletes: CategoryView[] }) => res.athletes),
    take(1)
  )

  private fetchDataFromQueryParams$ = combineLatest([this.distinctQueryParams$, this.queryChangeSubject]).pipe(
    // wait for user interaction (queryChangeSubject) before firing, so response differs from initialAthletes$
    switchMap(([qp, _]) => this.catService.searchAthletes$(qp))
  )

  private pageMeta$: Observable<SearchMeta | undefined> = this.pageMetaSubject.pipe(filter(meta => meta !== undefined))

  private readonly paginatedAthletes$ = this.fetchAthletes.pipe(
    withLatestFrom(this.distinctQueryParams$, this.pageMeta$),
    filter(([_, queryParams, meta]) => meta !== undefined && this.page < meta.pages),
    switchMap(([_, queryParams, meta]) => this.catService.searchAthletes$(queryParams, undefined, ++this.page)),
    tap(() => this.segmentInfiniteScrollCall(this.page))
  )

  public readonly athletes$: Observable<ReadonlyArray<CategoryView> | undefined> = merge(
    this.initialAthletes$,
    this.paginatedAthletes$,
    this.fetchDataFromQueryParams$
  ).pipe(
    scan<ReadonlyArray<CategoryView> | undefined, ReadonlyArray<CategoryView> | undefined>((acc, curr) => {
      return accumulateViewAll(acc, curr)
    }, []),
    map((views: ReadonlyArray<CategoryView>) => shouldShowViews(views)),
    tap(views => this.pageMetaSubject.next(views && views[0]?.meta)),
    shareReplay(1)
  )

  public updateSearch(query: Record<string, string>) {
    this.page = 1
    this.segment.trackEvent({
      name: SegmentEvents.SEARCH_QUERY_MADE,
      properties: {
        search_term: query.q || '',
        name: `Search for Athletes`
      }
    })
    this.router.navigate([], { queryParams: query })
    this.queryChangeSubject.next()
  }

  // Handle AdConfig
  public readonly adConfigMap = new Map<AdTypes, AdComponentInputs>()

  public ngOnInit() {
    this.setPageMeta()
    this.sendPageCall()
    this.setAdConfig()
  }

  public ngOnDestroy() {
    this.pageMeta.clearMeta()
  }

  private sendPageCall(): void {
    this.segment.pageV2(ATHLETE_LIST_TAG, { _tag: 'list' })
  }

  private segmentInfiniteScrollCall(currentPage: number): void {
    this.segment.trackEvent({
      name: SegmentEvents.INFINITE_SCROLL,
      properties: {
        page: currentPage
      }
    })
  }

  private setPageMeta(): void {
    this.vs.siteSettings$
      .pipe(
        map(settings => {
          const canonicalUrl = settings.core_site_url ? `${removeTrailingSlash(settings.core_site_url)}/people` : ''
          const pageMeta: PageMeta = {
            // TODO: update page-meta.service to allow for schema lists,
            // @see {@link https://developers.google.com/search/docs/advanced/structured-data/sd-policies#individual-items}
            type: 'website',
            twitterCard: 'summary_large_image',
            title: `${capitalizeFirstChar(settings.meta_sport_name || 'FloSports')} | Athletes - ${settings.site_name}`,
            description: `Check out your favorite athletes. ${settings.meta_sport_name} live events, videos, and news.`,
            canonicalUrl,
            siteName: settings.site_name,
            image: {
              url: settings.meta_default_image ?? '',
              height: DEFAULT_OG_IMAGE_HEIGHT,
              width: DEFAULT_OG_IMAGE_WIDTH
            }
          }
          this.pageMeta.setPageMeta(pageMeta)
        }),
        take(1)
      )
      .subscribe()
  }

  private setAdConfig() {
    // format the ad path,
    // see {@link https://docs.google.com/spreadsheets/d/1HzBmCrTPHRHgDJYlk-xmftJh8jkoe_rIykqH8gBtmYI/edit?usp=sharing}
    this.adConfigMap.set('skin', {
      config: {
        adConfig: AdConfig(`athletes/atf`, AD_SKIN_SIZE, 'ad-skin')
      }
    })

    this.adConfigMap.set('leaderboard-large', {
      config: {
        adConfig: AdConfig(`athletes/728x90_flex`, AD_LEADERBOARD_LARGE_FLEX_SIZE, 'leaderboard-large'),
        additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
      }
    })

    this.adConfigMap.set('leaderboard-small', {
      config: {
        adConfig: AdConfig(`athletes/728x90_flex`, AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small')
      }
    })

    this.adConfigMap.set('rectangle-small', {
      config: {
        adConfig: AdConfig(`athletes/300x250_1`, AD_RECTANGLE_LARGE_SIZE, 'rectangle-small')
      }
    })

    this.adConfigMap.set('adhesion', {
      config: {
        adConfig: AdConfig(`athletes/adhesion`, AD_ADHESION_SIZE, 'adhesion'),
        additionalOpts: { maxRefreshTime: 30000 }
      }
    })
  }
}
